import type { QueryClient } from '@tanstack/react-query';
import {
  materializePrivateAccount,
  type PrivateAccountEntity,
  privateAccountEntityKey,
  privateAccountScope,
  upsertPrivateAccountEntity,
} from '../../core/internal/private-account-entity.js';
import {
  type PublicUserEntity,
  publicUserEntityKey,
} from '../../core/internal/public-user-entity.js';
import type { MorfiUser } from './auth-state.js';

export function writeAuthPrivateAccount(
  queryClient: QueryClient,
  user: MorfiUser,
  viewerScope: string,
  networkScope: string,
): void {
  upsertPrivateAccountEntity(
    queryClient,
    viewerScope,
    networkScope,
    { ...user, userId: user.id },
    {
      source: 'optimistic',
      observedAtMs: Date.now(),
    },
  );
}

export function readAuthPrivateAccount(
  queryClient: QueryClient,
  user: MorfiUser,
  viewerScope: string,
  networkScope: string,
): MorfiUser {
  const scope = privateAccountScope(viewerScope, networkScope, user.id);
  const account = queryClient.getQueryData<PrivateAccountEntity>(
    privateAccountEntityKey(scope, user.id),
  );
  const publicUser = queryClient.getQueryData<PublicUserEntity>(publicUserEntityKey(user.id));
  const materialized = materializePrivateAccount(
    { ...user, heroNsfw: user.coverNsfw } as unknown as Record<string, unknown>,
    account,
    publicUser,
  );
  const heroNsfw = materialized.heroNsfw;
  const candidate: MorfiUser = {
    id: user.id,
    email: materialized.email as MorfiUser['email'],
    phoneNumber: materialized.phoneNumber as MorfiUser['phoneNumber'],
    displayName: materialized.displayName as MorfiUser['displayName'],
    handle: materialized.handle as MorfiUser['handle'],
    avatarUrl: materialized.avatarUrl as MorfiUser['avatarUrl'],
    avatarNsfw: materialized.avatarNsfw as MorfiUser['avatarNsfw'],
    avatarPreferences: materialized.avatarPreferences as MorfiUser['avatarPreferences'],
    heroUrl: materialized.heroUrl as MorfiUser['heroUrl'],
    coverNsfw: typeof heroNsfw === 'boolean' ? heroNsfw : user.coverNsfw,
    bio: materialized.bio as MorfiUser['bio'],
    country: materialized.country as MorfiUser['country'],
    locationSource: materialized.locationSource as MorfiUser['locationSource'],
    createdAt: materialized.createdAt as MorfiUser['createdAt'],
    onboardingCompleted: materialized.onboardingCompleted as MorfiUser['onboardingCompleted'],
    connectedProviders: materialized.connectedProviders as MorfiUser['connectedProviders'],
    turnkeySubOrgId: materialized.turnkeySubOrgId as MorfiUser['turnkeySubOrgId'],
  };
  return sameAuthUser(user, candidate) ? user : candidate;
}

export function subscribeAuthPrivateAccount(
  queryClient: QueryClient,
  userId: string,
  viewerScope: string,
  networkScope: string,
  listener: () => void,
): () => void {
  const scope = privateAccountScope(viewerScope, networkScope, userId);
  return queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated') return;
    const key = event.query.queryKey;
    if (
      (key[0] === 'accounts' && key[1] === 'viewer' && key[2] === scope && key[4] === userId) ||
      (key[0] === 'users' && key[1] === 'entity' && key[2] === userId)
    ) {
      listener();
    }
  });
}

function sameAuthUser(left: MorfiUser, right: MorfiUser): boolean {
  return (
    left.email === right.email &&
    left.phoneNumber === right.phoneNumber &&
    left.displayName === right.displayName &&
    left.handle === right.handle &&
    left.avatarUrl === right.avatarUrl &&
    left.avatarNsfw === right.avatarNsfw &&
    left.avatarPreferences === right.avatarPreferences &&
    left.heroUrl === right.heroUrl &&
    left.coverNsfw === right.coverNsfw &&
    left.bio === right.bio &&
    left.country === right.country &&
    left.locationSource === right.locationSource &&
    left.createdAt === right.createdAt &&
    left.onboardingCompleted === right.onboardingCompleted &&
    left.connectedProviders === right.connectedProviders &&
    left.turnkeySubOrgId === right.turnkeySubOrgId
  );
}
